import {
  blockSets,
  mapBlockSets,
  type DynamicLine,
  type Expr,
  type Mods,
  type SetLine,
  type Stmt,
} from './ast'
import { Parser } from './parser'
import { expr, serializeLine } from './serializer'

/**
 * The progressions the editor knows how to show as a form. A preset is data, not code: a
 * canonical block with holes in it and the fields that fill them. A block is that preset when
 * it unifies with the shape — so a block somebody typed by hand, or an assistant wrote, lights
 * up in the editor exactly as one the editor made. Anything that matches nothing is custom,
 * and is edited as code.
 */

export type FieldKind = 'count' | 'reps' | 'weight' | 'step'

export interface PresetField {
  readonly key: string
  readonly kind: FieldKind
  readonly label: string
}

export interface Preset {
  readonly id: string
  readonly label: string
  readonly fields: readonly PresetField[]
  readonly defaults: Readonly<Record<string, string>>
  /** The block, with `$name` where a field goes. */
  readonly shape: string
  readonly summary: (params: Readonly<Record<string, string>>) => string
}

export const PRESETS: readonly Preset[] = [
  {
    id: 'linear',
    label: 'Linear',
    fields: [
      { key: 'sets', kind: 'count', label: 'Sets' },
      { key: 'reps', kind: 'reps', label: 'Reps' },
      { key: 'start', kind: 'weight', label: 'Starting weight' },
      { key: 'step', kind: 'step', label: 'Add each time' },
    ],
    defaults: { sets: '3', reps: '5', start: '20kg', step: '2.5kg' },
    shape: [
      'dynamic {',
      '  let cleared = sessions.filter(s => s.sets.every(x => x.cleared)).length',
      '  yield $sets x $reps @ $start + $step * cleared',
      '}',
    ].join('\n'),
    summary: (p) => `${p.sets} x ${p.reps}, +${p.step} once you clear them all`,
  },
  {
    id: 'double',
    label: 'Double progression',
    fields: [
      { key: 'sets', kind: 'count', label: 'Sets' },
      { key: 'low', kind: 'reps', label: 'From reps' },
      { key: 'high', kind: 'reps', label: 'To reps' },
      { key: 'start', kind: 'weight', label: 'Starting weight' },
      { key: 'step', kind: 'step', label: 'Add each time' },
    ],
    defaults: { sets: '3', low: '8', high: '12', start: '20kg', step: '2.5kg' },
    shape: [
      'dynamic {',
      '  let topped = sessions.filter(s => s.sets.every(x => x.reps >= $high)).length',
      '  yield $sets x range($low, $high) @ $start + $step * topped',
      '}',
    ].join('\n'),
    summary: (p) => `${p.sets} x ${p.low}-${p.high}, +${p.step} once you reach ${p.high}`,
  },
]

/** The one line of a `dynamic` block, read on its own. */
function parseDynamic(source: string, holes: boolean): DynamicLine {
  const wrapped = `template "t" {\nworkout "w" {\nexercise "e" {\n${source}\n}\n}\n}`
  const template = new Parser(wrapped, holes).parseTemplate()
  const line = template.workouts[0]?.items[0]
  if (line?.type !== 'exercise') {
    throw new Error('A preset shape holds one exercise')
  }
  const first = line.exercise.lines[0]
  if (first?.type !== 'dynamic') {
    throw new Error('A preset shape is a dynamic block')
  }
  return first
}

/**
 * The same sources come through here again and again — every render of an editor asks about
 * the same handful of shapes — so what a source parses to is kept. The tree is never mutated
 * by anything downstream, so handing the same one back is safe.
 */
const parsed = new Map<string, DynamicLine>()

function readDynamic(source: string, holes: boolean): DynamicLine {
  const key = `${holes ? 'h' : 'p'} ${source}`
  const held = parsed.get(key)
  if (held) {
    return held
  }
  const line = parseDynamic(source, holes)
  // Filled shapes arrive one keystroke at a time, so the pile is emptied before it matters.
  if (parsed.size > 500) {
    parsed.clear()
  }
  parsed.set(key, line)
  return line
}

/** The block a preset makes for the values somebody typed into its form. */
export function buildPreset(
  preset: Preset,
  params: Readonly<Record<string, string>>,
  label?: string,
): DynamicLine {
  let source = preset.shape
  for (const field of preset.fields) {
    const typed = params[field.key]?.trim() ?? ''
    const written = typed === '' ? (preset.defaults[field.key] ?? '') : typed
    source = source.split(`$${field.key}`).join(written)
  }
  const built = readDynamic(source, false)
  const filled = { ...preset.defaults, ...params }
  return { ...built, label: label ?? preset.summary(filled) }
}

/** Which preset a block is, and what filled its holes — or null when it is custom. */
export function identifyPreset(
  line: DynamicLine,
): { preset: Preset; params: Record<string, string> } | null {
  for (const preset of PRESETS) {
    let shape: DynamicLine
    try {
      shape = readDynamic(preset.shape, true)
    } catch {
      continue
    }
    const bindings = new Map<string, Expr>()
    if (!unifyStmts(shape.body, bare(line).body, bindings)) {
      continue
    }
    const params: Record<string, string> = {}
    for (const [name, value] of bindings) {
      params[name] = expr(value)
    }
    // Verified the way the spec says: what the preset would build for these values must come
    // back as the same block, so a near-miss is never reported as a match.
    if (bodyText(buildPreset(preset, params)) === bodyText(line)) {
      return { preset, params }
    }
  }
  return null
}

/**
 * A block down to the shape of it. What it is called, and what surrounds the sets it yields —
 * the rest before them, whether they are warm-ups or filmed, anything written on them — is not
 * what decides which progression it is. A linear progression is still linear when it rests.
 */
function bodyText(line: DynamicLine): string {
  const out: string[] = []
  serializeLine(out, { ...bare(line), label: '', comments: [] }, 0)
  return out.join('')
}

/** The block with nothing around its sets: the shape, and only the shape. */
function bare(line: DynamicLine): DynamicLine {
  return mapBlockSets(line, (set) => ({ ...set, warmup: false, mods: {} }))
}

/** What a block asks for around its sets, read off the first line it yields. */
export function blockAround(line: DynamicLine): { warmup: boolean; mods: Mods } {
  const first = blockSets(line)[0]
  return { warmup: first?.warmup ?? false, mods: first?.mods ?? {} }
}

/** The same block with every set it yields asking for these. */
export function withBlockAround(
  line: DynamicLine,
  around: { warmup: boolean; mods: Mods },
): DynamicLine {
  return mapBlockSets(line, (set) => ({ ...set, warmup: around.warmup, mods: around.mods }))
}

function unifyStmts(
  pattern: readonly Stmt[],
  candidate: readonly Stmt[],
  bindings: Map<string, Expr>,
): boolean {
  if (pattern.length !== candidate.length) {
    return false
  }
  return pattern.every((stmt, index) => unifyStmt(stmt, candidate[index], bindings))
}

/** A candidate that is not there matches nothing, which is what a shorter block is. */
function unifyStmt(
  pattern: Stmt,
  candidate: Stmt | undefined,
  bindings: Map<string, Expr>,
): boolean {
  if (pattern.type !== candidate?.type) {
    return false
  }
  switch (pattern.type) {
    case 'declare': {
      const other = candidate as Extract<Stmt, { type: 'declare' }>
      return (
        pattern.mutable === other.mutable &&
        pattern.name === other.name &&
        unify(pattern.value, other.value, bindings)
      )
    }
    case 'yield': {
      const other = candidate as Extract<Stmt, { type: 'yield' }>
      return unifySetLine(pattern.line, other.line, bindings)
    }
    case 'return': {
      const other = candidate as Extract<Stmt, { type: 'return' }>
      if (pattern.line === null || other.line === null) {
        return pattern.line === other.line
      }
      return unifySetLine(pattern.line, other.line, bindings)
    }
    case 'expr': {
      const other = candidate as Extract<Stmt, { type: 'expr' }>
      return unify(pattern.expr, other.expr, bindings)
    }
    case 'assign': {
      const other = candidate as Extract<Stmt, { type: 'assign' }>
      return (
        pattern.op === other.op &&
        unify(pattern.target, other.target, bindings) &&
        unify(pattern.value, other.value, bindings)
      )
    }
    case 'if': {
      const other = candidate as Extract<Stmt, { type: 'if' }>
      return (
        unify(pattern.condition, other.condition, bindings) &&
        unifyStmts(pattern.then, other.then, bindings) &&
        unifyStmts(pattern.otherwise, other.otherwise, bindings)
      )
    }
    case 'forOf': {
      const other = candidate as Extract<Stmt, { type: 'forOf' }>
      return (
        pattern.name === other.name &&
        unify(pattern.source, other.source, bindings) &&
        unifyStmts(pattern.body, other.body, bindings)
      )
    }
    default:
      return false
  }
}

function unifySetLine(pattern: SetLine, candidate: SetLine, bindings: Map<string, Expr>): boolean {
  return (
    pattern.warmup === candidate.warmup &&
    unifyOptional(pattern.count, candidate.count, bindings) &&
    unify(pattern.primary, candidate.primary, bindings) &&
    unifyOptional(pattern.weight, candidate.weight, bindings) &&
    unifyOptional(pattern.mods.rest ?? null, candidate.mods.rest ?? null, bindings) &&
    pattern.mods.video === candidate.mods.video &&
    pattern.mods.notes === candidate.mods.notes
  )
}

function unifyOptional(
  pattern: Expr | null,
  candidate: Expr | null,
  bindings: Map<string, Expr>,
): boolean {
  if (pattern === null) {
    return candidate === null
  }
  if (candidate === null) {
    return false
  }
  return unify(pattern, candidate, bindings)
}

function unify(pattern: Expr, candidate: Expr | undefined, bindings: Map<string, Expr>): boolean {
  if (candidate === undefined) {
    return false
  }
  if (pattern.type === 'hole') {
    const bound = bindings.get(pattern.name)
    if (bound) {
      return expr(bound) === expr(candidate)
    }
    bindings.set(pattern.name, candidate)
    return true
  }
  if (pattern.type !== candidate.type) {
    return false
  }
  switch (pattern.type) {
    case 'number': {
      const other = candidate as Extract<Expr, { type: 'number' }>
      return pattern.value === other.value && pattern.unit === other.unit
    }
    case 'text': {
      const other = candidate as Extract<Expr, { type: 'text' }>
      return pattern.value === other.value
    }
    case 'name': {
      const other = candidate as Extract<Expr, { type: 'name' }>
      return pattern.name === other.name
    }
    case 'member': {
      const other = candidate as Extract<Expr, { type: 'member' }>
      return (
        pattern.name === other.name &&
        pattern.optional === other.optional &&
        unify(pattern.target, other.target, bindings)
      )
    }
    case 'index': {
      const other = candidate as Extract<Expr, { type: 'index' }>
      return (
        unify(pattern.target, other.target, bindings) && unify(pattern.index, other.index, bindings)
      )
    }
    case 'unary': {
      const other = candidate as Extract<Expr, { type: 'unary' }>
      return pattern.op === other.op && unify(pattern.operand, other.operand, bindings)
    }
    case 'binary': {
      const other = candidate as Extract<Expr, { type: 'binary' }>
      return (
        pattern.op === other.op &&
        unify(pattern.left, other.left, bindings) &&
        unify(pattern.right, other.right, bindings)
      )
    }
    case 'ternary': {
      const other = candidate as Extract<Expr, { type: 'ternary' }>
      return (
        unify(pattern.condition, other.condition, bindings) &&
        unify(pattern.then, other.then, bindings) &&
        unify(pattern.otherwise, other.otherwise, bindings)
      )
    }
    case 'arrow': {
      const other = candidate as Extract<Expr, { type: 'arrow' }>
      return (
        pattern.params.length === other.params.length &&
        pattern.params.every((name, index) => name === other.params[index]) &&
        unify(pattern.body, other.body, bindings)
      )
    }
    case 'array': {
      const other = candidate as Extract<Expr, { type: 'array' }>
      return (
        pattern.items.length === other.items.length &&
        pattern.items.every((item, index) => unify(item, other.items[index], bindings))
      )
    }
    case 'call': {
      const other = candidate as Extract<Expr, { type: 'call' }>
      return (
        unify(pattern.callee, other.callee, bindings) &&
        pattern.args.length === other.args.length &&
        pattern.args.every((arg, index) => unify(arg, other.args[index], bindings)) &&
        pattern.named.length === other.named.length &&
        pattern.named.every(([name], index) => name === other.named[index]?.[0]) &&
        pattern.named.every(([, value], index) => unify(value, other.named[index]?.[1], bindings))
      )
    }
    default:
      return false
  }
}
